// GWP: free gift picker for the cart. Markup comes from snippets/gwp-cart.liquid.
if (!customElements.get('gwp-picker')) {
  let gwpBusy = false;

  const postJson = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error('GWP cart request failed');
      return response.json();
    });

  class GwpPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
      this.reconcile();
    }

    get keys() {
      return (this.dataset.keys || '').split(',').filter(Boolean);
    }

    onClick(event) {
      const option = event.target.closest('[data-variant-id]');
      if (!option || option.disabled || option.getAttribute('aria-pressed') === 'true') return;
      event.preventDefault();
      this.run(async () => {
        await this.removeGifts();
        await postJson('/cart/add.js', {
          items: [{ id: Number(option.dataset.variantId), quantity: 1, properties: { _gwp: 'true' } }],
        });
      });
    }

    // Keeps the cart honest after any change: no gift under the threshold, one gift at most.
    reconcile() {
      const count = Number(this.dataset.count);
      if (count === 0) return;
      const eligible = this.dataset.eligible === 'true';
      if (eligible && count === 1 && Number(this.dataset.qty) === 1) return;

      if (!eligible || count > 1) {
        this.run(() => this.removeGifts());
      } else {
        this.run(() => postJson('/cart/change.js', { id: this.keys[0], quantity: 1 }));
      }
    }

    removeGifts() {
      if (!this.keys.length) return Promise.resolve();
      const updates = {};
      this.keys.forEach((key) => {
        updates[key] = 0;
      });
      return postJson('/cart/update.js', { updates });
    }

    async run(task) {
      if (gwpBusy) return;
      gwpBusy = true;
      this.setAttribute('aria-busy', 'true');
      try {
        await task();
        await this.refresh();
      } catch (error) {
        console.error(error);
        this.removeAttribute('aria-busy');
      } finally {
        gwpBusy = false;
      }
    }

    async refresh() {
      const drawerBody = this.closest('#CartDrawer-Body');
      if (!drawerBody || window.location.pathname === window.routes.cart_url) {
        window.location.reload();
        return;
      }

      const section = this.dataset.section;
      const response = await fetch(window.routes.cart_url + '?sections=' + section + ',cart-counter');
      const sections = await response.json();
      const parser = new DOMParser();

      const nextBody = parser
        .parseFromString(sections[section], 'text/html')
        .querySelector('#CartDrawer-Body');
      if (nextBody) drawerBody.innerHTML = nextBody.innerHTML;

      const counter = document.getElementById('cart-counter');
      if (counter && sections['cart-counter']) {
        const nextCounter = parser
          .parseFromString(sections['cart-counter'], 'text/html')
          .querySelector('#cart-counter');
        if (nextCounter) counter.innerHTML = nextCounter.innerHTML;
      }
    }
  }

  customElements.define('gwp-picker', GwpPicker);
}

// Same picker for the Recharge mini cart, which replaces the theme drawer. It lives in a shadow
// root, so it is rendered from /cart.js here. Keep these values in step with snippets/gwp-cart.liquid.
(() => {
  const THRESHOLD = 20000;
  const GIFTS = [
    { handle: 'the-catalyst', variantId: 39748008509622, name: 'The Catalyst Serum' },
    { handle: 'the-solution', variantId: 39817973072054, name: 'The Solution Exfoliator' },
    { handle: 'the-purifier', variantId: 39704992088246, name: 'The Purifier Cleanser' },
  ];
  const HOST_ID = 'recharge-storefront-experiences__cart-drawer';
  const PANEL_ID = 'gwp-drawer-panel';
  const STYLES = '.gwpd{margin:0 16px 12px;padding:12px;border:1px solid #e5e5e5;border-radius:8px;font-size:12px;color:inherit}' +
    '.gwpd__title{margin:0;font-weight:600;font-size:14px}.gwpd__note{margin:2px 0 0;opacity:.7}' +
    '.gwpd__options{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:10px}' +
    '.gwpd__option{display:block;width:100%;padding:6px;border:1px solid #d4d4d4;border-radius:6px;background:transparent;color:inherit;font:inherit;font-size:11px;line-height:1.3;text-align:center;cursor:pointer}' +
    '.gwpd__option[aria-pressed="true"]{border-color:#111;box-shadow:inset 0 0 0 1px #111}' +
    '.gwpd__option[disabled]{opacity:.45;cursor:not-allowed}' +
    '.gwpd__option[disabled] img{filter:grayscale(1)}' +
    '.gwpd__option img{display:block;width:100%;height:auto;margin-bottom:6px}' +
    '.gwpd[aria-busy="true"]{opacity:.5;pointer-events:none}';

  let root = null;
  let gifts = null;
  let busy = false;
  let timer = null;
  let lastRender = '';

  const postJson = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error('GWP cart request failed');
      return response.json();
    });

  const money = (cents) => '$' + (cents / 100).toFixed(2);
  const isGift = (item) => Boolean(item.properties && item.properties._gwp);

  const loadGifts = async () => {
    if (gifts) return gifts;
    gifts = await Promise.all(
      GIFTS.map(async (gift) => {
        const product = await fetch('/products/' + gift.handle + '.js').then((response) => response.json());
        const variant = product.variants.find((item) => item.id === gift.variantId);
        const image = (variant.featured_image && variant.featured_image.src) || product.featured_image;
        return {
          id: variant.id,
          title: gift.name,
          size: variant.title,
          image: image ? image + (image.includes('?') ? '&' : '?') + 'width=160' : '',
          available: variant.available,
        };
      })
    );
    return gifts;
  };

  const refreshDrawer = () => {
    if (window.location.pathname === window.routes.cart_url) {
      window.location.reload();
    } else if (typeof window.refreshRadianceCart === 'function') {
      window.refreshRadianceCart();
    }
  };

  const qualifyingTotal = (cart) => {
    const items = cart.items.filter((item) => !isGift(item));
    const lines = items.reduce((total, item) => total + item.final_line_price, 0);
    const orderDiscounts = (cart.cart_level_discount_applications || []).reduce(
      (total, discount) => total + discount.total_allocated_amount,
      0
    );
    return lines - orderDiscounts;
  };

  const hideGiftControls = (cart) => {
    const section = root.querySelector('#cart-drawer section');
    if (!section) return;
    Array.from(section.children).forEach((row, index) => {
      const gift = cart.items[index] && isGift(cart.items[index]);
      const stepper = row.querySelector('input[aria-label="Quantity"]');
      if (stepper) stepper.parentElement.style.display = gift ? 'none' : '';
      row.querySelectorAll('button').forEach((button) => {
        if (/subscription/i.test(button.textContent)) button.style.display = gift ? 'none' : '';
      });
    });
  };

  const render = (cart, total, selected) => {
    const section = root.querySelector('#cart-drawer section');
    let panel = root.getElementById(PANEL_ID);
    if (!section || cart.item_count === 0 || cart.currency !== 'USD') {
      if (panel) panel.remove();
      lastRender = '';
      return;
    }

    const eligible = total >= THRESHOLD;
    const state = [eligible, total, selected].join('|');
    if (panel && state === lastRender && panel.previousElementSibling === section) return;

    if (!panel) {
      panel = document.createElement('div');
      panel.id = PANEL_ID;
      panel.addEventListener('click', onPanelClick);
    }

    const heading = eligible
      ? '<p class="gwpd__title">Choose your free gift</p>'
      : '<p class="gwpd__title">Spend ' + money(THRESHOLD - total) + ' more to choose a free gift</p>';
    const options =
      '<div class="gwpd__options">' +
      gifts
        .map(
          (gift) =>
            '<button type="button" class="gwpd__option" data-variant-id="' + gift.id + '"' +
            ' aria-pressed="' + (gift.id === selected) + '"' + (eligible && gift.available ? '' : ' disabled') + '>' +
            (gift.image ? '<img src="' + gift.image + '" alt="" loading="lazy">' : '') +
            '<span>' + gift.title + '<br>' + gift.size + '</span></button>'
        )
        .join('') +
      '</div>';

    panel.innerHTML =
      '<style>' + STYLES + '</style><div class="gwpd">' + heading +
      '<p class="gwpd__note">Complimentary with orders of $200+</p>' + options + '</div>';
    section.after(panel);
    lastRender = state;
  };

  const sync = async () => {
    if (busy || !root || !root.querySelector('#cart-drawer')) return;
    busy = true;
    try {
      await loadGifts();
      const cart = await fetch('/cart.js').then((response) => response.json());
      const giftLines = cart.items.filter(isGift);
      const total = qualifyingTotal(cart);
      const eligible = total >= THRESHOLD;

      if (giftLines.length && (!eligible || giftLines.length > 1)) {
        const updates = {};
        giftLines.forEach((item) => {
          updates[item.key] = 0;
        });
        await postJson('/cart/update.js', { updates });
        refreshDrawer();
      } else if (giftLines.length === 1 && giftLines[0].quantity > 1) {
        await postJson('/cart/change.js', { id: giftLines[0].key, quantity: 1 });
        refreshDrawer();
      } else {
        render(cart, total, giftLines.length ? giftLines[0].variant_id : 0);
        hideGiftControls(cart);
      }
    } catch (error) {
      console.error(error);
    } finally {
      busy = false;
    }
  };

  const queueSync = () => {
    clearTimeout(timer);
    timer = setTimeout(sync, 300);
  };

  async function onPanelClick(event) {
    const option = event.target.closest('[data-variant-id]');
    if (!option || option.disabled || option.getAttribute('aria-pressed') === 'true') return;
    busy = true;
    const box = event.currentTarget.querySelector('.gwpd');
    box.setAttribute('aria-busy', 'true');
    try {
      const cart = await fetch('/cart.js').then((response) => response.json());
      const updates = {};
      cart.items.filter(isGift).forEach((item) => {
        updates[item.key] = 0;
      });
      if (Object.keys(updates).length) await postJson('/cart/update.js', { updates });
      await postJson('/cart/add.js', {
        items: [{ id: Number(option.dataset.variantId), quantity: 1, properties: { _gwp: 'true' } }],
      });
      refreshDrawer();
    } catch (error) {
      console.error(error);
    } finally {
      box.removeAttribute('aria-busy');
      busy = false;
      queueSync();
    }
  }

  const attach = () => {
    const host = document.getElementById(HOST_ID);
    if (!host || !host.shadowRoot) return false;
    root = host.shadowRoot;
    new MutationObserver((records) => {
      const outside = records.some((record) => !(record.target.closest && record.target.closest('#' + PANEL_ID)));
      if (outside) queueSync();
    }).observe(root, { childList: true, subtree: true, characterData: true });
    queueSync();
    return true;
  };

  if (!attach()) {
    const poll = setInterval(() => {
      if (attach()) clearInterval(poll);
    }, 1000);
  }
})();
